'use client';

import NextImage, { ImageProps as NextImageProps } from 'next/image';
import React from 'react';

import { cn } from '@/lib/utils';
import { useBoolean } from '@/hooks/utils';
import { Skeleton } from './skeleton';

export interface ImageProps extends Omit<NextImageProps, 'onError' | 'onLoad'> {
  fallbackSrc?: string;
  className?: string;
  showSkeleton?: boolean;
  showBrokenImage?: boolean;
  containerClassName?: string;
  unoptimized?: boolean;
  quality?: number;
}

// How often the same url is attempted before the broken tile is shown. The
// image service occasionally answers slowly under load; reloading the page
// used to be the only cure.
const MAX_LOAD_ATTEMPTS = 3;

// How long to wait before each retry. The retries used to fire back to back,
// so all three hit the same slow response within a few milliseconds and the
// tile ended up broken anyway -- which is why QA still had to reload, several
// times, to get an image back. One entry per retry; the browser gets a real
// pause to let the image service catch up.
const RETRY_DELAYS_MS = [500, 2000];

function retryDelayFor(attempt: number) {
  return RETRY_DELAYS_MS[attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1] ?? 0;
}

// After the broken tile is shown, keep checking the original quietly and swap it
// back in if it starts working. This covers a different failure to the retries
// above, which is why the delays are so much longer.
//
// Measured on staging 2026-08-17: of 20 gallery urls, 2 answered 404 to every
// attempt inside the 2.5s the retries above cover, and both answered 200 about a
// minute later without anything being changed. The image service derives a size
// on first request and 404s until that finishes, so the recovery horizon is
// minutes, not seconds. That is exactly what QA has been reporting as "needs a
// reload or two": the reload is not fixing anything, it is just buying time.
//
// Deliberately probed rather than re-rendered: the broken tile appears on time
// and only becomes an image once the original is known to load, so this cannot
// flip between the two the way clearing the error on load would (see below).
const RECOVERY_PROBE_DELAYS_MS = [15000, 45000];

/**
 * Enhanced Image component with loading and error states
 */
const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      containerClassName,
      src,
      alt,
      fallbackSrc = '/images/fallback/placeholder.svg',
      showSkeleton = true,
      showBrokenImage = true,
      unoptimized = false,
      placeholder,
      blurDataURL,
      quality = 75,
       ...props
    },
    ref
  ) => {
    const { value: isLoading, onFalse: handleLoad, onTrue: startLoading } = useBoolean(true);
    const { value: hasError, onTrue: markFailed, onFalse: clearError } = useBoolean(false);
    // Deliberately NOT clearing the error on load: after a failure the element
    // shows the fallback, so its load event says "the fallback arrived", not
    // "the original recovered". Clearing here would swap back to the failing
    // src and flip between the two forever.
    const [attempt, setAttempt] = React.useState(0);
    const retryTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const recoveryTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const recoveryProbe = React.useRef<HTMLImageElement | null>(null);
    // Rounds already spent on this src, so a url that keeps failing cannot probe
    // forever. Reset with the src below.
    const recoveryRound = React.useRef(0);

    const cancelPendingRetry = React.useCallback(() => {
      if (retryTimer.current !== null) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    }, []);

    const cancelRecovery = React.useCallback(() => {
      if (recoveryTimer.current !== null) {
        clearTimeout(recoveryTimer.current);
        recoveryTimer.current = null;
      }
      if (recoveryProbe.current !== null) {
        recoveryProbe.current.onload = null;
        recoveryProbe.current.onerror = null;
        recoveryProbe.current = null;
      }
    }, []);

    // If src is null, undefined, or empty string, treat it as an error case
    // and use the fallback image instead
    const isSrcMissing = src === null || src === undefined || src === '';
    const effectiveHasError = isSrcMissing || hasError;
    // A failed load falls back to the placeholder image; a missing src goes
    // straight to it.
    const effectiveSrc = effectiveHasError === true ? fallbackSrc : src;
    const isBrokenPlaceholderShown =
      showBrokenImage === true && hasError === true && isSrcMissing === false;

    // A failed load must not outlive the src it failed on: without this reset
    // one transient image error (a slow or rate limited proxy response) left
    // the image marked broken for the whole session, and only a page reload
    // brought it back.
    React.useEffect(() => {
      cancelPendingRetry();
      cancelRecovery();
      recoveryRound.current = 0;
      clearError();
      startLoading();
      setAttempt(0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    // A retry scheduled for an image that has since been unmounted would set
    // state on a dead component.
    React.useEffect(() => cancelPendingRetry, [cancelPendingRetry]);
    React.useEffect(() => cancelRecovery, [cancelRecovery]);

    // The broken tile is showing. Ask for the original again on a long horizon,
    // using a detached element so the visible tile is untouched until the
    // original actually loads, then swap it in.
    React.useEffect(() => {
      if (hasError === false || isSrcMissing === true) {
        return undefined;
      }

      let isActive = true;

      const scheduleProbe = () => {
        const delay = RECOVERY_PROBE_DELAYS_MS[recoveryRound.current];
        if (delay === undefined) {
          return;
        }
        recoveryRound.current += 1;

        recoveryTimer.current = setTimeout(() => {
          recoveryTimer.current = null;
          if (isActive === false) {
            return;
          }
          const probe = new window.Image();
          recoveryProbe.current = probe;
          probe.onload = () => {
            recoveryProbe.current = null;
            if (isActive === false) {
              return;
            }
            // Remounting on a new attempt is what re-requests the url.
            clearError();
            startLoading();
            setAttempt(current => current + 1);
          };
          probe.onerror = () => {
            recoveryProbe.current = null;
            if (isActive === false) {
              return;
            }
            scheduleProbe();
          };
          probe.src = src as string;
        }, delay);
      };

      scheduleProbe();

      return () => {
        isActive = false;
        cancelRecovery();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasError, isSrcMissing, src]);

    // A single failure is usually the image service answering slowly under
    // load, which is exactly what a page reload cured. Give the same url a
    // couple of fresh attempts (a new element re-requests it) before showing
    // the broken tile, so the user never has to reload. The skeleton stays up
    // in the meantime, so a retry never flashes the broken tile first.
    const handleError = React.useCallback(() => {
      if (attempt >= MAX_LOAD_ATTEMPTS - 1) {
        markFailed();
        return;
      }
      cancelPendingRetry();
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        setAttempt(attempt + 1);
      }, retryDelayFor(attempt));
    }, [attempt, markFailed, cancelPendingRetry]);

    return (
      <div className={cn('relative overflow-hidden', containerClassName)}>
        {isLoading === true && showSkeleton === true && placeholder !== 'blur' && (
          <div className="absolute inset-0">
            <Skeleton className="h-full w-full" />
          </div>
        )}
        {isBrokenPlaceholderShown === true && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
            <NextImage
              src="/images/broken/broken.svg"
              alt=""
              width={60}
              height={60}
              className="opacity-70"
              unoptimized={true}
            />
          </div>
        )}
        {/* The image element stays mounted even while the broken placeholder
            covers it, so a retry (a new src, or the browser retrying) can
            still succeed and reveal the image. */}
        <NextImage
          // Remounting on a new attempt is what makes the browser request the
          // same url again instead of keeping the failed result.
          key={attempt}
          ref={ref}
          {...props}
          src={effectiveSrc}
          alt={alt || ''}
          className={cn(
            'transition-opacity duration-300',
            isLoading === true || isBrokenPlaceholderShown === true
              ? 'opacity-0'
              : 'opacity-100',
            effectiveHasError === true ? 'grayscale-[50%]' : '',
            className
          )}
          onError={handleError}
          onLoad={handleLoad}
          placeholder={placeholder}
          blurDataURL={blurDataURL}
          unoptimized={unoptimized}
          quality={quality}
         />
      </div>
    );
  }
);

Image.displayName = 'Image';

export { Image }; 